import * as React from 'react'
import { useMemo } from 'react'
import { Props as ResultItemProps } from '../typings'
import { useCheckCustomizeSettings } from '@user/features/GlobalSearch/ResultItem/hooks/useCheckCustomizeSettings'
import { NameValue } from '@user/features/GlobalSearch/ResultItem/NameValue/NameValue'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

export type Props = Pick<
  ResultItemProps,
  'searchItem' | 'searchText' | 'onLocationClick' | 'recent'
> & {
  highlightColor: string
}

export function CustomResults(props: Props) {
  const { searchItem } = props
  const {
    email,
    totalValue,
    age,
    createdBy,
    services,
    createdTime,
    owner,
    ownerIsActive,
    entityType,
    salesFunnel,
    salesTeam,
  } = searchItem
  const { formatDateTime } = useLocalizationDateFormat()

  const createdTimeFormatted = useMemo(
    () =>
      !!createdTime
        ? formatDateTime(createdTime.toString(), " 'at' h:mm aa") || ''
        : '',
    [createdTime, formatDateTime],
  )

  const ownerValue = React.useMemo(() => {
    if (
      ['Lead', 'Opportunity'].includes(entityType) &&
      ownerIsActive === false
    ) {
      return `${owner} (Inactive)`
    }
    return owner
  }, [owner, ownerIsActive, entityType])

  const {
    totalValueShow,
    emailShow,
    ageShow,
    timeShow,
    createdByShow,
    servicesShow,
    ownerShow,
    salesFunnelShow,
    salesTeamShow,
  } = useCheckCustomizeSettings(searchItem)

  return (
    <>
      {totalValueShow && (
        <NameValue
          {...props}
          name={'Total Value'}
          value={totalValue}
          variant="currency"
        />
      )}
      {emailShow && (
        <NameValue
          {...props}
          name={'Email'}
          value={email}
          variant="string"
          highlight={true}
        />
      )}
      {ageShow && (
        <NameValue
          {...props}
          name={'Age'}
          value={`${age.toString()} day${age === 1 ? '' : 's'}`}
          variant="string"
        />
      )}
      {timeShow && (
        <NameValue
          {...props}
          name={'Date Created'}
          value={createdTimeFormatted}
          variant="string"
        />
      )}
      {createdByShow && (
        <NameValue
          {...props}
          name={'Created By'}
          value={createdBy ?? '-'}
          variant="string"
        />
      )}
      {servicesShow && (
        <NameValue
          {...props}
          name={'Services'}
          value={services}
          variant="array"
        />
      )}
      {ownerShow && (
        <NameValue
          {...props}
          name={'Owner'}
          value={ownerValue}
          variant="string"
        />
      )}
      {salesFunnelShow && (
        <NameValue
          {...props}
          name={'Sales Funnel'}
          value={salesFunnel}
          variant="string"
        />
      )}
      {salesTeamShow && (
        <NameValue
          {...props}
          name={'Sales Team'}
          value={salesTeam}
          variant="string"
        />
      )}
    </>
  )
}
